// @flow
import * as React from 'react';
import Portal from './../common/Portal';
import { findDOMNode } from 'react-dom';
import _ from 'lodash';
import { CommentFilterOption } from '../util/Constants';
import {
  COMMENTS,
  COMMENT_FILTER_BY,
  ACTIVE_COMMENT_ID,
  SHOW_COMMENTS,
  WAVEPLAYER_COMMENT,
  COMMENT_TEXT,
  COLLECTIONS,
  RECENT_DELETED_COMMENT_ID
} from '../store/Constants';
import StoreListener from '../store/StoreListener';
import { AnnotationThread, RepoItem } from '../autogenerated/ZoomBridge';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
import FileInfoUtil from '../util/FileInfoUtil';
import AddCommentForm from '../tabs/collections/AddCommentForm';
import Store from '../store/Store';
import SpatialComment from './SpatialComment';
import { getErrorMessage } from '../util/ErrorUtil';

import './WithVideoComments.css';

const debug = require('../debug/debug')('wc:comments:WithVideoComments');

const storeItems = [
  { name: COMMENTS, defaultValue: [] },
  { name: COMMENT_FILTER_BY, defaultValue: CommentFilterOption.UNRESOLVED },
  { name: ACTIVE_COMMENT_ID, defaultValue: null },
  { name: SHOW_COMMENTS, defaultValue: true },
  { name: WAVEPLAYER_COMMENT, defaultValue: null }
];

type Props = {
  selectedAsset: RepoItem,
  blockKeyEvents: boolean,
  comments: AnnotationThread[],
  COMMENTS: AnnotationThread[],
  COMMENT_FILTER_BY: string,
  ACTIVE_COMMENT_ID: ?number,
  SHOW_COMMENTS: boolean,
  WAVEPLAYER_COMMENT: ?AnnotationThread,
  onProgress: Function,
  onCommentButtonClick: Function,
  useLocalComments: boolean
};

type State = {
  audioComment: ?AnnotationThread,
  comments: AnnotationThread[]
};

const WithVideoComments = (getVideoNode) => (Component) =>
  @StoreListener(storeItems)
class WithVideoComments extends React.Component<Props, State> {
  static WrappedComponent = Component;

  state = {
    audioComment: null,
    comments: []
  };
  _timesToComments: Object = {};
  _didPlayJustStart: boolean = false;

  async componentDidMount() {
    this.addListeners();
    if (this.props.useLocalComments) {
      const comments = await this.fetchComments(this.props);
      this.setTimes(comments);
      this.setState({ comments });
    }
    if (this._component && this._component._component && this._component._component._videoRef) {
      this._component._component._videoRef.requestVideoFrameCallback(this.videoFramCallBackFn)
    }
  }


  getFrameByTime = (time: number, frameRate: number) => {
    const frame = (Math.trunc(Math.round((time - Math.trunc(time)) * frameRate)) - 1).toString();
    const number = Math.trunc(time).toString();
    return parseFloat(number + '.' + frame);

  }


  videoFramCallBackFn = (timePass, frameMetadata) => {
    let timeToCommentArr = Object.keys(this._timesToComments);
    if (timeToCommentArr.length) {
      for (let i = 0; i < timeToCommentArr.length; i++) {
        const key = timeToCommentArr[i];
        const comment = this._timesToComments[key];
        const frameRate = this._component._component.controller._frameRate;
        const commentTimeWithFrames = this.getFrameByTime(key / 1000, frameRate);
        const currentTimeWithFrame = this.getFrameByTime(frameMetadata.mediaTime, frameRate);
        if (currentTimeWithFrame === commentTimeWithFrames) {
          this._component._component.seek(key, comment);
          break;
        }
      }
    }
    if (this._component && this._component._component && this._component._component._videoRef) {
      this._component._component._videoRef.requestVideoFrameCallback(this.videoFramCallBackFn)
    }
  };



  componentWillUnmount() {
    this.removeListeners();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps[COMMENTS] !== this.props[COMMENTS] || nextProps.comments !== this.props.comments) {
      this.setTimes(this.getComments(nextProps));
    }
    if (nextProps[ACTIVE_COMMENT_ID] !== -1 && nextProps[ACTIVE_COMMENT_ID] !== Store.get(RECENT_DELETED_COMMENT_ID)) {
      if (nextProps[ACTIVE_COMMENT_ID] !== this.props[ACTIVE_COMMENT_ID]) {
        this.seekToActiveComment(nextProps);
      }
    }
  }

  addListeners = () => {
    const node = getVideoNode(this._component._component);
    if (node && node.addEventListener) {
      node.addEventListener('play', this.onPlay);
      //node.addEventListener('canplaythrough', this.canplaythrough);
    }
  };

  removeListeners = () => {
    const node = getVideoNode(this._component._component);
    if (node && node.removeEventListener) {
      node.removeEventListener('play', this.onPlay);
      //node.removeEventListener('canplaythrough',this.canplaythrough);
    }
  };

  // canplaythrough = () =>{
  //   console.log("LOADED")
  //   this.seekToActiveComment(this.props);
  // }

  onPlay = () => {
    this._didPlayJustStart = true;
    setTimeout(() => {
      this._didPlayJustStart = false;
    }, 100);
  };

  setTimes = (comments: AnnotationThread[]) => {
    this._timesToComments = {};
    comments.forEach((c) => {
      const time = AnnotationUtil.getVideOffsetInMilliseconds(c);
      this._timesToComments[time] = c;
    });
  };

  seekToActiveComment = (props: Props) => {
    const id: ?number = props[ACTIVE_COMMENT_ID];
    const comments: AnnotationThread[] = this.getComments(props);
    const comment: ?AnnotationThread = comments.find((c) => c.getThreadId() === id);
    if (!comment) {
      return;
    }

    const time = AnnotationUtil.getVideOffsetInMilliseconds(comment);
    if (!time) {
      return;
    }

    debug('[seekToActiveComment()] seeking to ', time);
    this._component._component.pause();
    this._component._component.seek(time, comment);
  };

  getCollection = () => {
    const collections = Store.get(COLLECTIONS);
    return collections.find((c) => c.getCollectionId() === parseInt(window.ARGS.collectionId, 10));
  };

  saveAnnotation = async (comment: AnnotationThread) => {
    const { selectedAsset } = this.props;
    const collection = this.getCollection();
    const fuidRrnInfo = AnnotationUtil.toFuidRrnInfo(
      selectedAsset.getFuid(),
      selectedAsset.getBid(),
      selectedAsset.getRrn()
    );
    const collectionId = collection.getCollectionId();
    const res = await AnnotationUtil.saveAnnotation(fuidRrnInfo, comment, collectionId);
    const savedComment = res.getThreads()[0];

    if (!savedComment.getThreadId()) {
      return;
    }

    const comments = this.getComments()
      .filter((c) => c.getThreadId() !== savedComment.getThreadId())
      .concat(savedComment);

    this.updateComments(comments);
  };

  fetchComments = (props: Props) => {
    const { selectedAsset } = props;
    const collection = this.getCollection();

    const info = AnnotationUtil.repoItemToFuidRrnInfo(selectedAsset);
    return new Promise((resolve) => {
      AnnotationUtil.getAnnotations(info, collection.getCollectionId(), 0, (getAnnotations) => {
        const annotations = getAnnotations.getAnnotations() || [];
        resolve(annotations);
      });
    });
  };

  getComments = (props: Props = this.props): AnnotationThread[] => {
    if (props.useLocalComments) {
      return props.comments || this.state.comments;
    }

    return props[COMMENTS];
  };

  updateComments = (comments: AnnotationThread[]) => {
    if (this.props.useLocalComments) {
      this.setState({ comments });
    } else {
      Store.put(COMMENTS, comments);
    }
  };

  onRef = (component) => {
    this._component = component;
  };

  onProgress = (time: number) => {
    const { onProgress } = this.props;
    if (onProgress) {
      onProgress(time);
    }
  };

  onCommentButtonClick = (millis: number) => {
    const { selectedAsset } = this.props;
    const { audioComment } = this.state;
    const isAudio = FileInfoUtil.isAudio(selectedAsset);
    if (!isAudio) {
      return this.props.onCommentButtonClick(millis);
    }

    if (audioComment) {
      this.setState({ audioComment: null });
      Store.put(COMMENT_TEXT, '');
      return;
    }

    this._component._component.pause();
    this.setState({ audioComment: AnnotationUtil.createAudioComment(millis) });
  };

  onSubmit = async (comment: AnnotationThread, isAudioComment: boolean) => {
    try {
      await this.saveAnnotation(comment);
      Store.put(COMMENT_TEXT, '');
      if (isAudioComment) {
        this.setState({ audioComment: null });
      } else {
        Store.put(WAVEPLAYER_COMMENT, null);
      }
    } catch (err) {
      debug('[onSubmit()] error: ', err);
      window.setError(getErrorMessage(err));
      throw err;
    }
  };

  onWaveplayerCommentClose = () => {
    Store.put(WAVEPLAYER_COMMENT, null);
    Store.put(COMMENT_TEXT, '');
  };

  onTimelineCommentRef = (node: HTMLDivElement) => {
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const parentRect = node.parentElement.getBoundingClientRect();
    const triangle = node.querySelector('.triangle');

    if (rect.left < 0) {
      node.style.transform = 'none';
      node.style.left = '1px';
    } else if (rect.right > parentRect.right) {
      node.style.left = '';
      node.style.transform = 'none';
      node.style.right = '1px';
    }

    setTimeout(() => {
      const offsetLeft = rect.left;
      const newOffsetLeft = node.getBoundingClientRect().left;
      const diff = newOffsetLeft - offsetLeft;
      triangle.style.left = `${170 - diff}px`;
    }, 10);
  };

  onWaveplayerCommentRef = (spatialComment: SpatialComment) => {
    if (!spatialComment) {
      return;
    }

    const node = findDOMNode(spatialComment);
    const commentNode = node.querySelector('.comment');
    const rect = commentNode.getBoundingClientRect();
    const parentRect = node.parentElement.getBoundingClientRect();

    let transform = [];
    if (rect.bottom > parentRect.bottom) {
      transform.push(`translateY(-${rect.height - 6}px)`);
    }

    if (rect.right > parentRect.right) {
      transform.push(`translateX(-${rect.width - 6}px)`);
    }

    commentNode.style.transform = transform.join(' ');
  };

  renderAudioComment = (querySelector: string) => {
    const { audioComment } = this.state;

    if (!audioComment) {
      return null;
    }
    let leftPadding = 0;
    if (this.props.compareViewRevisionIndex == 1) {
      let halfscreenWidth = window.innerWidth / 2;
      leftPadding = halfscreenWidth;
    }

    let top = window.innerHeight - 240,
      left = 100;

    if (querySelector) {
      const node = document.querySelector(querySelector);
      if (node) {
        const rect = node.getBoundingClientRect();
        const scale = this._component._component.getDurationMillis() / rect.width;
        const millis = AnnotationUtil.getVideOffsetInMilliseconds(audioComment);
        top = rect.top - 213;
        left = millis / scale;
      }
      left = left + leftPadding;
    }

    const style = {
      left,
      top,
      background: 'var(--primary-header-bg)'
    };

    const props = {
      comment: audioComment,
      closeOnOutsideClick: false,
      onSubmit: (comment: AnnotationThread) => this.onSubmit(comment, true),
      onClose: () => this.setState({ audioComment: null })
    };

    return (
      <Portal defaultOpen={true} closeOnEsc>
        <div
          className="timeline-comment"
          name="WithVideoComments_Portal"
          style={style}
          ref={this.onTimelineCommentRef}>
          <AddCommentForm {...props} />
          <div className="triangle" />
        </div>
      </Portal>
    );
  };

  renderWaveplayerComment = () => {
    const waveplayerComment = this.props.WAVEPLAYER_COMMENT;

    if (!waveplayerComment) {
      return null;
    }

    const style = {
      top: AnnotationUtil.getHeightOffset(waveplayerComment) - 16,
      left: AnnotationUtil.getWidthOffset(waveplayerComment) - 16
    };

    const props = {
      ref: this.onWaveplayerCommentRef,
      comment: waveplayerComment,
      style,
      isActive: true,
      onSubmit: (comment: AnnotationThread) => this.onSubmit(comment, false),
      onClose: this.onWaveplayerCommentClose,
      onMarkerClick: _.noop
    };

    return <SpatialComment {...props} />;
  };

  render() {
    const { blockKeyEvents } = this.props;
    const { audioComment } = this.state;

    const props = {
      ...this.props,
      ref: this.onRef,
      comments: this.getComments(),
      blockKeyEvents: blockKeyEvents || !!audioComment,
      onProgress: this.onProgress,
      onCommentButtonClick: this.onCommentButtonClick,
      renderAudioComment: this.renderAudioComment,
      renderWaveplayerComment: this.renderWaveplayerComment
    };

    return <Component {...props} />;
  }
};

  export default WithVideoComments;
